/**
 * What destroys the jet on contact (GDD §4.4; concept L1117–L1126), all tested in the flat top-down plane: terrain
 * above 1.4 m (a bank or the island), an enemy's box against the jet's ±3.6 × ±5 m box, an intact bridge within
 * 7 m, an empty tank. Enemy fire is tested in src/entities/shots.ts. Nothing collides while the jet is invulnerable
 * or during the first second of a run's title fade.
 */
import { config } from '../core/config.ts';
import type { GameSim } from '../sim/context.ts';
import type { CrashCause, EnemyType } from '../sim/state.ts';
import { killEnemy } from '../entities/enemies.ts';
import { crash } from './crash.ts';

/** Collisions start 1 s into the title fade (concept L1118). */
const TITLE_GRACE = 3.5;
/** An empty tank (concept L1125). */
const EMPTY = 0.001;

export function collisionCause(type: EnemyType): CrashCause {
  return type === 'ship' ? 'gunboat' : type === 'heli' ? 'heli' : 'jet';
}

export function checkCollisions(sim: GameSim): void {
  const run = sim.run;
  if (!run.alive || run.invuln > 0 || run.title > TITLE_GRACE) return;
  const c = config.crash;
  const jet = sim.jet;
  if (sim.ground(jet.x, jet.z) > c.bankHeight) {
    crash(sim, 'riverbank');
    return;
  }
  const ents = sim.ents;
  for (let i = 0; i < ents.count; i++) {
    const e = ents.items[i];
    if (!e || e.dying > 0 || e.type === 'fuel') continue;
    if (Math.abs(jet.x - e.x) < e.hw + c.jetBox.halfWidth && Math.abs(jet.z - e.z) < e.hl + c.jetBox.halfLength) {
      killEnemy(sim, e, true);
      crash(sim, collisionCause(e.type));
      return;
    }
  }
  const b = sim.bridge;
  if (b.intact && Math.abs(jet.z - b.z) < c.bridgeDz) {
    crash(sim, 'bridge');
    return;
  }
  if (run.fuel <= EMPTY) crash(sim, 'fuel');
}
